import { useEffect, useContext } from 'react';
import ChatUserContext from '../../Models/ChatUserContext';
import { getGenesysCustomAttributes } from '../../Utils/genesysHelpers';

/**
 * GenesysWidget - Official Genesys Web Messenger Widget
 *
 * Uses the official Genesys SDK instead of custom headless implementation.
 * This is more reliable and requires less configuration.
 *
 * Prerequisites in Genesys Cloud Admin:
 *   1. Create Web Messaging Deployment (generates Deployment ID)
 *   2. Configure Messenger appearance and behavior
 *   3. Add your domain to Allowed Domains
 *   4. Configure bot/agent routing
 *
 * Environment Variables:
 *   REACT_APP_GENESYS_MESSENGER_DEPLOYMENT_ID - Your deployment ID from Genesys
 *   REACT_APP_GENESYS_WS_REGION - Platform region (e.g., 'mypurecloud.ie' or 'euw1')
 */
const GenesysWidget = () => {
    const { userInfo } = useContext(ChatUserContext);

    useEffect(() => {
        const deploymentId = process.env.REACT_APP_GENESYS_MESSENGER_DEPLOYMENT_ID || '6b571b90-2d89-4b75-942c-5e532a5ac201';
        const region = process.env.REACT_APP_GENESYS_WS_REGION || 'mypurecloud.ie';

        // Map platform domain to environment code
        const regionMap = {
            'mypurecloud.ie': 'euw1',
            'mypurecloud.com': 'useast1',
            'mypurecloud.de': 'euc1',
            'mypurecloud.jp': 'apne1',
            'mypurecloud.com.au': 'aps1',
        };

        const environment = regionMap[region.toLowerCase()] || region;
        const scriptUrl = `https://apps.${region}/genesys-bootstrap/genesys.min.js`;

        console.log('🚀 Loading Genesys Widget:', {
            deploymentId,
            region,
            environment,
            scriptUrl,
        });

        // Check if already loaded
        if (window.Genesys) {
            console.log('⚠️ Genesys already loaded, skipping initialization');
            return;
        }

        // Load Genesys Messenger SDK
        (function (g, e, n, es, ys) {
            g['_genesysJs'] = e;
            g[e] = g[e] || function () {
                (g[e].q = g[e].q || []).push(arguments);
            };
            g[e].t = 1 * new Date();
            g[e].c = es;
            ys = document.createElement('script');
            ys.async = 1;
            ys.src = n;
            ys.charset = 'utf-8';

            // Add error handler
            ys.onerror = function () {
                console.error('❌ Failed to load Genesys script from:', n);
                console.error('Check:');
                console.error('1. Deployment ID is correct');
                console.error('2. Region is correct');
                console.error('3. Deployment is Active in Genesys Admin');
                console.error('4. Network connection is working');
            };

            ys.onload = function () {
                console.log('✅ Genesys script loaded successfully');
            };

            document.head.appendChild(ys);
        })(window, 'Genesys', scriptUrl, {
            environment: environment,
            deploymentId: deploymentId,
        });

        // Subscribe to widget events
        window.Genesys('subscribe', 'Launcher.ready', function () {
            console.log('✅ Genesys Widget Ready - Widget should be visible now!');
        });

        window.Genesys('subscribe', 'Launcher.opened', function () {
            console.log('✅ Genesys Widget Opened');
        });

        window.Genesys('subscribe', 'Launcher.closed', function () {
            console.log('🔌 Genesys Widget Closed');
        });

        window.Genesys('subscribe', 'MessagingService.conversationCleared', function () {
            console.log('🗑️ Conversation Cleared');
        });

        window.Genesys('subscribe', 'MessagingService.messageSent', function (data) {
            console.log('📤 Chat log [message sent]:', data);
        });

        window.Genesys('subscribe', 'MessagingService.messageReceived', function (data) {
            console.log('📥 Chat log [message received]:', data);
        });

        window.Genesys('subscribe', 'MessagingService.error', function (error) {
            console.error('❌ Genesys Error:', error);
        });

        // Add timeout to check if widget loaded
        setTimeout(() => {
            if (!window.Genesys || !window.Genesys.c) {
                console.error('❌ Genesys widget failed to load after 10 seconds');
                console.error('Troubleshooting:');
                console.error('1. Check browser console for script loading errors');
                console.error('2. Verify deployment ID:', deploymentId);
                console.error('3. Verify region:', region);
                console.error('4. Check Genesys Admin - deployment must be Active');
                console.error('5. Check Genesys Admin - "Show Widget" must be enabled');
            }
        }, 10000);

    }, []);

    // Update custom attributes when userInfo changes
    useEffect(() => {
        if (window.Genesys && userInfo && (userInfo.PatientEmail || userInfo.PatientCellNumber || userInfo.MedicalAidNumber)) {
            const customAttributes = getGenesysCustomAttributes(userInfo);
            console.log('📋 Updating custom attributes:', customAttributes);

            window.Genesys('command', 'Database.set', {
                messaging: {
                    customAttributes: customAttributes,
                },
            });
        }
    }, [userInfo]);

    // This component doesn't render anything - the Genesys widget is injected by the SDK
    return null;
};

/**
 * Helper function to programmatically open the Genesys widget
 * Can be called from anywhere in the app
 */
export function openGenesysWidget(customAttributes = {}) {
    const tryOpen = (attempt = 0) => {
        if (!window.Genesys) {
            if (attempt < 20) {
                setTimeout(() => tryOpen(attempt + 1), 250);
                return;
            }

            console.warn('Genesys widget not loaded yet');
            return;
        }

        const openLauncher = () => {
            window.Genesys('command', 'Messenger.open');
        };

        if (Object.keys(customAttributes).length > 0) {
            window.Genesys('command', 'Database.set', {
                messaging: {
                    customAttributes,
                },
            });

            // Give Genesys a brief moment to persist custom attributes before opening launcher.
            setTimeout(openLauncher, 150);
            return;
        }

        openLauncher();
    };

    tryOpen();
}

/**
 * Helper function to programmatically close the Genesys widget
 */
export function closeGenesysWidget() {
    if (window.Genesys) {
        window.Genesys('command', 'Messenger.close');
    }
}

/**
 * Helper function to send a message programmatically
 */
export function sendGenesysMessage(text, customAttributes = {}) {
    if (window.Genesys) {
        // Set custom attributes if provided
        if (Object.keys(customAttributes).length > 0) {
            window.Genesys('command', 'Database.set', {
                messaging: {
                    customAttributes: customAttributes,
                },
            });
        }

        // Open widget and send message
        window.Genesys('command', 'Messenger.open');

        // Note: There's no direct API to send a message programmatically
        // The user will need to type and send the message
        console.log('📤 Widget opened. User can now send:', text);
    }
}

export default GenesysWidget;
